import { CloudOff, Loader2, Pause, Play, RefreshCcw, Square, TriangleAlert } from "lucide-react";
import { Button } from "@akuna/ui";
import { isTimerRunning } from "../transcription/machine-state";
import type { TranscriptionMachineState } from "../transcription/types";

interface MobileRecordingBarProps {
  state: TranscriptionMachineState;
  statusLabel: string;
  durationLabel: string;
  processingLabel: string;
  failedLabel: string;
  disconnectedLabel: string;
  openLabel: string;
  pauseResumeLabel: string;
  stopLabel: string;
  retryLabel: string;
  reconnectLabel: string;
  onOpen: () => void;
  onPauseResume: () => void;
  onStop: () => void;
  onRetry: () => void;
  onReconnect: () => void;
}

export function MobileRecordingBar({
  state,
  statusLabel,
  durationLabel,
  processingLabel,
  failedLabel,
  disconnectedLabel,
  openLabel,
  pauseResumeLabel,
  stopLabel,
  retryLabel,
  reconnectLabel,
  onOpen,
  onPauseResume,
  onStop,
  onRetry,
  onReconnect,
}: MobileRecordingBarProps) {
  const isBusyProcessing = state === "finalizing" || state === "analyzing";
  const isFailed = state === "analysis_failed";
  const isInterrupted = state === "interrupted";
  const isPaused = state === "paused";
  const showTimer = isTimerRunning(state);

  return (
    <div className="flex shrink-0 items-center gap-2 border-b border-primary/20 bg-secondary px-3 py-1.5">
      <button type="button" className="flex min-w-0 flex-1 items-center gap-2 text-left" onClick={onOpen} aria-label={openLabel}>
        {isBusyProcessing ? (
          <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-primary" />
        ) : isInterrupted ? (
          <CloudOff className="h-3.5 w-3.5 shrink-0 text-destructive" />
        ) : isFailed ? (
          <TriangleAlert className="h-3.5 w-3.5 shrink-0 text-destructive" />
        ) : (
          <span className="h-2.5 w-2.5 shrink-0 animate-pulse rounded-full bg-primary motion-reduce:animate-none" />
        )}
        {!isInterrupted && (
          <span className="hidden truncate text-[13px] font-bold text-primary sm:inline">{statusLabel}</span>
        )}
        {isBusyProcessing ? (
          <span className="text-[13px] font-bold text-primary">{processingLabel}</span>
        ) : isInterrupted ? (
          <span className="truncate text-[13px] font-bold text-destructive">{disconnectedLabel}</span>
        ) : isFailed ? (
          <span className="text-[13px] font-bold text-destructive">{failedLabel}</span>
        ) : !isPaused ? (
          <span className="flex items-center gap-[3px]" aria-hidden="true">
            <span className="h-3.5 w-[3px] animate-[akwave_0.9s_ease-in-out_infinite] rounded-sm bg-primary" style={{ animationDelay: "0ms" }} />
            <span className="h-3.5 w-[3px] animate-[akwave_0.9s_ease-in-out_infinite] rounded-sm bg-primary" style={{ animationDelay: "150ms" }} />
            <span className="h-3.5 w-[3px] animate-[akwave_0.9s_ease-in-out_infinite] rounded-sm bg-primary" style={{ animationDelay: "300ms" }} />
            <span className="h-3.5 w-[3px] animate-[akwave_0.9s_ease-in-out_infinite] rounded-sm bg-primary" style={{ animationDelay: "450ms" }} />
            <span className="h-3.5 w-[3px] animate-[akwave_0.9s_ease-in-out_infinite] rounded-sm bg-primary" style={{ animationDelay: "250ms" }} />
          </span>
        ) : null}
        {showTimer && (
          <span className="ml-auto font-mono text-xs font-semibold text-primary">{durationLabel}</span>
        )}
      </button>
      {isInterrupted ? (
        <>
          <Button
            variant="outline"
            size="icon"
            className="h-[34px] w-[34px] rounded-full"
            onClick={onReconnect}
            aria-label={reconnectLabel}
          >
            <RefreshCcw className="h-4 w-4" />
          </Button>
          <Button size="icon" className="h-[34px] w-[34px] rounded-full" onClick={onStop} aria-label={stopLabel}>
            <Square className="h-3.5 w-3.5 fill-current" />
          </Button>
        </>
      ) : isFailed ? (
        <Button variant="outline" size="icon" className="h-[34px] w-[34px] rounded-full" onClick={onRetry} aria-label={retryLabel}>
          <RefreshCcw className="h-4 w-4" />
        </Button>
      ) : (
        <>
          <Button
            variant="outline"
            size="icon"
            className="h-[34px] w-[34px] rounded-full"
            onClick={onPauseResume}
            disabled={isBusyProcessing}
            aria-label={pauseResumeLabel}
          >
            {isPaused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
          </Button>
          <Button
            size="icon"
            className="h-[34px] w-[34px] rounded-full"
            onClick={onStop}
            disabled={isBusyProcessing}
            aria-label={stopLabel}
          >
            <Square className="h-3.5 w-3.5 fill-current" />
          </Button>
        </>
      )}
    </div>
  );
}
